/**
 * Today (daily reports) hooks — talks to /v1/stores/reports on the shared api.
 *
 * Contract mirrors the cockpit UI's lib/useStores.ts report hooks so the two
 * clients stay compatible with the same store_report / store_report_photo
 * tables. Notable shape rules the api enforces:
 *   - phase enum is `open` | `midday` | `close` (NOT `opening` / `closing`).
 *   - `date` (YYYY-MM-DD) is required on every POST.
 *   - HQ scoping: GET takes `?store_no=`, POST takes `store_no` in the body.
 *   - Photos use a presign flow: POST /reports/{id}/photos returns
 *     { photo_id, key, upload_url }, then the browser PUTs the raw bytes to
 *     upload_url with the file's own Content-Type.
 *   - Checklist is [{item_id, checked}] where item_ids come from
 *     GET /v1/stores/me's per-phase checklist array.
 *
 * The business day is the STORE's day, resolved server-side from
 * retail_stores.timezone — every store-scoped endpoint already defaults its
 * own "today" that way. This client only needs its default `date` to agree,
 * so the hooks below read the zone off the store object useStoreMe already
 * fetched rather than assuming a single timezone for every store.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiGet, apiPost, type ApiError } from './api';
import { STORES_API_BASE, useStoreMe } from './useStores';
import { HOME_SUMMARY_KEY } from './useHomeSummary';

export type Phase = 'open' | 'midday' | 'close';

export type ReportChecklistEntry = { item_id: number; checked: boolean };

export type ReportPhoto = {
  photo_id: number;
  key: string;
  url?: string | null;
};

export type PhaseReport = {
  report_id: number;
  submitted_at: string;
  traffic_count: number | null;
  traffic_source: 'auto' | 'manual' | null;
  transactions: number | null;
  note: string | null;
  checklist: ReportChecklistEntry[];
  photos: ReportPhoto[];
};

export type PhaseEntry = {
  submitted: boolean;
  report: PhaseReport | null;
};

export type DailyReport = {
  date: string;
  store_no: number;
  phases: {
    open: PhaseEntry;
    midday: PhaseEntry;
    close: PhaseEntry;
  };
  traffic_auto: {
    traffic: number | null;
    transactions: number | null;
  } | null;
};

export type SubmitPhaseInput = {
  phase: Phase;
  traffic_count?: number;
  traffic_source?: 'auto' | 'manual';
  transactions?: number;
  note?: string;
  checklist?: ReportChecklistEntry[];
};

export type SubmitPhaseResult = {
  report_id: number;
  phase: Phase;
  report: PhaseReport;
};

export type PhotoPresign = {
  photo_id: number;
  key: string;
  upload_url: string;
};

const REPORTS_ROUTE = `${STORES_API_BASE}/reports`;

// YYYY-MM-DD for the given IANA zone, falling back to America/Denver when the
// zone is missing (store timezone not yet loaded/configured) or invalid.
export function todayInStoreZone(timezone?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone ?? 'America/Denver',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Denver',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }
}

function reportsQKey(storeNo: number | undefined, date: string) {
  return ['stores', 'reports', storeNo ?? 'own', date] as const;
}

export function useDailyReport(
  storeNo?: number,
  date?: string,
): UseQueryResult<DailyReport | null, Error> {
  const meQ = useStoreMe(storeNo);
  const resolvedDate = date ?? todayInStoreZone(meQ.data?.store?.timezone);
  const params = new URLSearchParams({ date: resolvedDate });
  if (storeNo !== undefined) params.set('store_no', String(storeNo));
  const url = `${REPORTS_ROUTE}?${params.toString()}`;
  return useQuery<DailyReport | null, Error>({
    queryKey: reportsQKey(storeNo, resolvedDate),
    queryFn: async () => {
      try {
        return await apiGet<DailyReport>(url);
      } catch (err) {
        // 404 = no report yet today; treat as an empty shell rather than error.
        if ((err as ApiError).status === 404) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useSubmitReport(storeNo?: number, date?: string) {
  const meQ = useStoreMe(storeNo);
  const resolvedDate = date ?? todayInStoreZone(meQ.data?.store?.timezone);
  const qc = useQueryClient();
  return useMutation<SubmitPhaseResult, Error, SubmitPhaseInput>({
    mutationFn: (input) =>
      apiPost<SubmitPhaseResult>(REPORTS_ROUTE, {
        date: resolvedDate,
        ...(storeNo !== undefined ? { store_no: storeNo } : {}),
        ...input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportsQKey(storeNo, resolvedDate) });
      // Home tile badge (`today.pending_phases`) is derived from the same
      // reports data — invalidate so the Today "!" chip clears on submit.
      void qc.invalidateQueries({ queryKey: HOME_SUMMARY_KEY });
    },
  });
}

// Presign + browser PUT. Deliberately does NOT use apiFetch for the PUT:
//   - S3 rejects requests where the client sends headers it didn't sign
//     (apiFetch always sets `Accept: application/json`).
//   - `credentials: 'include'` would leak the ampersandbrands.com session
//     cookie to the S3 host, which we do not want.
// The Content-Type is taken from the File so S3 stores it verbatim.
export function useUploadReportPhoto(storeNo?: number, date?: string) {
  const meQ = useStoreMe(storeNo);
  const resolvedDate = date ?? todayInStoreZone(meQ.data?.store?.timezone);
  const qc = useQueryClient();
  return useMutation<
    { photo_id: number; key: string },
    Error,
    { reportId: number; file: File }
  >({
    mutationFn: async ({ reportId, file }) => {
      const presign = await apiPost<PhotoPresign>(
        `${REPORTS_ROUTE}/${reportId}/photos`,
        {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          ...(storeNo !== undefined ? { store_no: storeNo } : {}),
        },
      );
      const putRes = await fetch(presign.upload_url, {
        method: 'PUT',
        body: file,
        headers: file.type ? { 'Content-Type': file.type } : undefined,
      });
      if (!putRes.ok) {
        const err = new Error(`Photo upload failed (${putRes.status})`) as ApiError;
        err.status = putRes.status;
        err.body = await putRes.text().catch(() => null);
        throw err;
      }
      return { photo_id: presign.photo_id, key: presign.key };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reportsQKey(storeNo, resolvedDate) });
    },
  });
}
